/*
 * Voyado autocomplete — IIFE bound to [data-voyado-autocomplete] (the search
 * drawer's predictive-search host). Typed q → phrase chips + Products/Pages tabs
 * in the node; empty q → phrase chips into [data-search-drawer-suggested-terms]
 * + TOP_PRODUCTS into [data-search-drawer-trending]. Chip click →
 * PHRASE_SUGGEST. Listens to the drawer's search-drawer:query event. A global
 * drawer singleton, so it boots once (no section lifecycle).
 */
;(function () {

  // Ajax routes need the market root, or Shopify answers in the default market. routes.root ends in '/'.
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  'use strict';

  const DEBOUNCE_MS = 250;
  // Unconfigured page reference so the body-supplied recommendation list applies
  // (a configured page's Experience-app config would otherwise override it).
  const TRENDING_REF = '/search-drawer-trending';

  // Clone a [data-tmpl-ac-*] template + read its [data-slot] hooks (shared cloners
  // from voyado-card-renderer.js, read at call time). Classes live in the markup.
  function tmpl(name) { return window.voyadoTmpl(name); }
  function slot(root, name) { return window.voyadoSlot(root, name); }

  function init(node) {
    if (node.__voyadoAutocomplete) return;
    node.__voyadoAutocomplete = true;

    let timer = null;
    let zeroTerm = false;
    // Empty-state recovery: two retries with a widening gap, then give up quietly.
    const ZERO_TERM_RETRIES = 2;
    const ZERO_TERM_RETRY_MS = 700;
    let zeroTermRetries = 0;
    let currentQuery = '';
    let gen = 0; // fetch generation — a stale autocomplete response bails before render
    // True while the current query came from a phrase-suggestion chip. The in-drawer
    // refine tags the autocomplete query origin=PHRASE_SUGGEST, but Elevate's
    // relevance model wants that origin on the resulting SEARCH-PAGE query too. So we
    // carry the flag and stamp origin=PHRASE_SUGGEST onto the search-page navigation
    // (view-all pill + Enter submit). Any organic keystroke resets it via runFetch.
    let phraseSuggested = false;
    // Last-rendered chips { row, phrases }, kept so a resize can rebuild + re-pack.
    let lastFit = null;
    // Products/Pages cap from the theme setting (data-search-drawer-results-limit,
    // default 12, range 1-24). Voyado's autocomplete has no limit param — the base
    // product count is set in the Voyado autocomplete template — so this is a
    // client-side ceiling applied to both tabs. The ceiling is a performance guard;
    // larger lists slow the drawer render.
    const drawerRoot = node.closest('[data-search-drawer]');
    const rawLimit = drawerRoot && parseInt(drawerRoot.getAttribute('data-search-drawer-results-limit'), 10);
    const RESULTS_LIMIT = rawLimit > 0 ? Math.min(24, rawLimit) : 12;
    // Trending cap from the theme setting (data-search-drawer-trending-limit, default
    // 12, range 2-24). Sent as `limit` on the TOP_PRODUCTS list below.
    const rawTrendingLimit = drawerRoot && parseInt(drawerRoot.getAttribute('data-search-drawer-trending-limit'), 10);
    const TRENDING_LIMIT = rawTrendingLimit > 0 ? Math.min(24, rawTrendingLimit) : 12;
    // The Experience-app content list that feeds the Pages tab. The id MUST match a content list
    // configured in Elevate — an arbitrary id 400s; 'content-hits' is the configured list here.
    // Overridable per store via data-search-drawer-content-list-id.
    const CONTENT_LIST_ID = (drawerRoot && drawerRoot.getAttribute('data-search-drawer-content-list-id')) || 'content-hits';

    function scheduleFetch(query) {
      clearTimeout(timer);
      const q = (query || '').trim();
      if (q.length === 0) { runFetch('', 'ORGANIC'); return; }
      timer = setTimeout(function () { runFetch(q); }, DEBOUNCE_MS);
    }

    function runFetch(query, origin) {
      const myGen = ++gen;
      const myZero = query === '';
      zeroTerm = myZero;
      currentQuery = query || '';
      phraseSuggested = origin === 'PHRASE_SUGGEST'; // organic typing (no origin) resets to false
      node.setAttribute('data-voyado-state', 'loading');
      // Products, phrases AND the Pages tab's content all come from one Voyado autocomplete call: the
      // contentLists body asks Elevate for content items (articles/guides/…) alongside the products.
      // (No Shopify predictive-search fallback — the Pages tab is fully Elevate-sourced; it depends on
      // the Elevate content feed being populated, otherwise it shows no pages.)
      window.VoyadoClient.autocomplete(
        { q: query, origin: origin || 'ORGANIC', presentCustom: 'swatch_image_url|product_badges|colour_name' },
        { contentLists: [{ id: CONTENT_LIST_ID, limit: RESULTS_LIMIT }] }
      )
        .then(function (res) { if (myGen === gen) { zeroTermRetries = 0; render(res); } })
        .catch(function () {
          if (myGen !== gen) return; // a newer query superseded this one
          // The empty state is fetched once on page load, so a dropped request leaves the drawer
          // with nothing to show. Retry silently — a failure here is not one the shopper can act on.
          if (myZero && zeroTermRetries < ZERO_TERM_RETRIES) {
            zeroTermRetries++;
            setTimeout(function () { runFetch('', 'ORGANIC'); }, ZERO_TERM_RETRY_MS * zeroTermRetries);
            return;
          }
          node.setAttribute('data-voyado-state', 'error');
          if (!myZero) { announce('Search results are unavailable'); setViewAll(false); } // TODO(i18n)
        });
    }

    // Feed the drawer's sr-only aria-live region (the drawer owns the announcer).
    function announce(msg) {
      const drawer = node.closest('[data-search-drawer]');
      const region = drawer && drawer.querySelector('[data-search-drawer-announcer]');
      if (region) region.textContent = msg;
    }

    // Reveal/hide the drawer's "View all results" pill (+ mobile link). The
    // drawer owns the markup; we only toggle it from the result state.
    function setViewAll(visible, total) {
      const drawer = node.closest('[data-search-drawer]');
      if (!drawer) return;
      const wrap = drawer.querySelector('[data-search-drawer-view-all-wrap]');
      const pill = wrap && wrap.querySelector('[data-search-drawer-view-all]');
      const mobile = drawer.querySelector('[data-search-drawer-view-all-mobile]');
      const form = drawer.querySelector('[data-search-drawer-form]');
      const base = (form && form.getAttribute('action'))
        || (pill && (pill.getAttribute('href') || '').split('?')[0]) || '';
      // Stamp origin=PHRASE_SUGGEST when the query came from a phrase chip so the
      // search-page query Elevate receives carries the phrase→search signal.
      const originSuffix = phraseSuggested ? '&origin=PHRASE_SUGGEST' : '';
      const href = (visible && currentQuery) ? base + '?q=' + encodeURIComponent(currentQuery) + originSuffix : base;
      if (wrap) wrap.setAttribute('data-state', visible ? 'visible' : 'hidden');
      if (pill && visible) {
        pill.setAttribute('href', href);
        // Show the total products found (Voyado totalHits); fall back to the static
        // label when the app omits the total.
        const label = pill.querySelector('.action-pill__text');
        if (label) label.textContent = (typeof total === 'number') ? ('View all ' + total + ' results') : 'View all results'; // TODO(i18n)
      }
      if (mobile) {
        if (visible) { mobile.setAttribute('href', href); mobile.classList.remove('hidden'); mobile.removeAttribute('aria-hidden'); }
        else { mobile.classList.add('hidden'); mobile.setAttribute('aria-hidden', 'true'); }
      }
    }

    // Centred no-results block — head text filled with the query (textContent escapes it).
    function renderNoResults() {
      const wrap = tmpl('ac-no-results');
      slot(wrap, 'head').textContent = currentQuery ? 'No results for “' + currentQuery + '”.' : 'No results found.'; // TODO(i18n)
      return wrap;
    }

    function render(res) {
      const suggestions = (res && res.productSuggestions) || [];
      // Each suggestion is a colour group (products = colour siblings), like the
      // PLP productGroups. Render ONE card per group with its siblings so the
      // swatches + colour hover-swap appear (not one card per colour).
      const groups = suggestions
        .map(function (s) { return (s && s.products) || []; })
        .filter(function (ps) { return ps.length; })
        .slice(0, RESULTS_LIMIT); // theme setting caps the Products tab
      const phrases = (res && res.phraseSuggestions) || [];
      if (zeroTerm) { renderEmptyState(phrases); return; }
      // Total matched products for the phrase (Voyado `totalHits`; omitted if disabled in the app).
      const totalHits = (res && typeof res.totalHits === 'number') ? res.totalHits : null;
      // Pages tab is fully Elevate-sourced — the content items from the autocomplete contentLists.
      renderResults(phrases, groups, contentPages(res), totalHits);
    }

    // Map Elevate autocomplete contentLists → the Pages-tab item shape ({ title, url, ticket }).
    // Carrying the content item's ticket lets a click fire the same Elevate click-notify as the
    // product cards (via the document [data-voyado-ticket] seam).
    function contentPages(res) {
      const items = (res && res.contentLists && res.contentLists[0] && res.contentLists[0].items) || [];
      return items
        .map(function (it) { return { title: it && it.title, url: it && it.link, ticket: it && it.ticket }; })
        .filter(function (p) { return p.title || p.url; })
        .slice(0, RESULTS_LIMIT);
    }

    function renderResults(phrases, groups, pages, totalHits) {
      pages = pages || [];
      if (groups.length === 0 && phrases.length === 0 && pages.length === 0) {
        node.replaceChildren(renderNoResults());
        node.setAttribute('data-voyado-state', 'empty');
        setViewAll(false);
        announce('No results found'); // TODO(i18n)
        return;
      }
      const frag = document.createDocumentFragment();
      const chips = renderChips(phrases);
      if (chips) frag.appendChild(chips);

      // Products / Pages tabs. Default to products unless there are only pages;
      // the CSS hides the inactive panel off the tablist's
      // data-search-drawer-active-tab, so tablist + both panels must be siblings.
      const activeTab = (groups.length === 0 && pages.length > 0) ? 'pages' : 'products';
      // Products tab shows the Voyado total (`totalHits`), not just the capped preview
      // count; falls back to the shown count when the app omits the total.
      frag.appendChild(renderTabs(totalHits != null ? totalHits : groups.length, pages.length, activeTab));

      frag.appendChild(renderGrid(groups, 'ac-grid-products')); // grid IS the products panel (attrs in the template)

      frag.appendChild(renderPagesPanel(pages));

      node.replaceChildren(frag);
      const chipRow = node.querySelector('[data-search-drawer-chips]');
      if (chipRow) fitChips(chipRow, phrases);
      document.dispatchEvent(new CustomEvent('voyado:cards-rendered', { detail: { root: node } }));
      node.setAttribute('data-voyado-state', 'results');
      setViewAll(groups.length > 0, totalHits);
      // TODO(i18n)
      announce(groups.length === 0 ? 'No results found'
        : groups.length === 1 ? '1 result' : groups.length + ' results');
    }

    // Tablist (Products / Pages). A tab with a zero count is disabled.
    function renderTabs(productsCount, pagesCount, activeTab) {
      const tablist = tmpl('ac-tablist');
      tablist.setAttribute('data-search-drawer-active-tab', activeTab);
      tablist.appendChild(tabButton('products', 'Products', productsCount, activeTab === 'products')); // TODO(i18n)
      tablist.appendChild(tabButton('pages', 'Pages', pagesCount, activeTab === 'pages')); // TODO(i18n)
      return tablist;
    }

    function tabButton(key, label, count, selected) {
      const b = tmpl('ac-tab');
      b.id = 'search-drawer-tab-' + key;
      b.setAttribute('data-search-drawer-tab', key);
      b.setAttribute('aria-controls', 'search-drawer-panel-' + key);
      b.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (count === 0) b.disabled = true;
      b.textContent = label + ' (' + count + ')';
      return b;
    }

    // Pages tab panel — a link list of Elevate content items ({ title, url, ticket }). The item's
    // ticket on the link makes a click notify Elevate via the document [data-voyado-ticket] seam.
    function renderPagesPanel(pages) {
      const panel = tmpl('ac-pages');
      (pages || []).forEach(function (p) {
        const title = p && p.title;
        const raw = p && p.url;
        if (!title && !raw) return;
        // Central allowlist then market root (voyado-card-renderer.js) — same guards, same order as
        // the cards: prefixing first would turn a rejected scheme into a path the allowlist accepts.
        const safe = window.voyadoSafeUrl ? window.voyadoSafeUrl(raw) : raw;
        const url = (safe && window.voyadoWithMarket ? window.voyadoWithMarket(safe) : safe) || '#';
        const a = tmpl('ac-page');
        a.href = url;
        if (p && p.ticket) a.setAttribute('data-voyado-ticket', p.ticket);
        slot(a, 'text').textContent = title || raw;
        panel.appendChild(a);
      });
      return panel;
    }

    // Phrase chips above the curated popular/help block; trending grid below.
    function renderEmptyState(phrases) {
      const drawer = node.closest('[data-search-drawer]');
      const terms = drawer && drawer.querySelector('[data-search-drawer-suggested-terms]');
      const trending = drawer && drawer.querySelector('[data-search-drawer-trending]');
      const chips = renderChips(phrases);
      if (terms) {
        terms.replaceChildren();
        if (chips) {
          terms.appendChild(chips);
          const chipRow = terms.querySelector('[data-search-drawer-chips]');
          if (chipRow) fitChips(chipRow, phrases);
        }
      }
      if (trending) fetchTrending(trending);
      node.replaceChildren();
      node.setAttribute('data-voyado-state', 'empty');
      setViewAll(false); // browse state — no "view all" pill
      announce(''); // browse state (no query) — clear any prior result count
    }

    // Trending = TOP_PRODUCTS recommendation via the landingPage body. `limit` is the
    // theme's performance cap (data-search-drawer-trending-limit, default 12, max 24);
    // Elevate itself would allow up to 200.
    function fetchTrending(target) {
      window.VoyadoClient.landingPage(
        { pageReference: TRENDING_REF, presentCustom: 'swatch_image_url|product_badges|colour_name' },
        { recommendationLists: [{ id: 'drawer-trending', algorithm: 'TOP_PRODUCTS', limit: TRENDING_LIMIT }] }
      ).then(function (res) {
        const list = res && res.recommendationLists && res.recommendationLists[0];
        // Keep the colour group per product (don't flatten) so trending cards show swatches.
        const groups = ((list && list.productGroups) || [])
          .map(function (g) { return (g && g.products) || []; })
          .filter(function (ps) { return ps.length; });
        target.replaceChildren();
        if (groups.length > 0) {
          target.appendChild(renderTrending(groups));
          document.dispatchEvent(new CustomEvent('voyado:cards-rendered', { detail: { root: target } }));
        }
      }).catch(function () { target.replaceChildren(); });
    }

    function renderChips(phrases) {
      if (!phrases || phrases.length === 0) return null;
      const wrap = tmpl('ac-chips');
      const row = slot(wrap, 'row');
      buildChips(row, phrases);
      if (!row.children.length) return null; // nothing left after dropping the typed term
      return wrap;
    }

    // (Re)build the chips (no line budgeting). Skip a phrase equal to the typed term —
    // it would just echo the query; zero-term browse has no query, so it keeps all.
    function buildChips(row, phrases) {
      row.replaceChildren();
      const typed = (currentQuery || '').trim().toLowerCase();
      (phrases || []).forEach(function (p) {
        const q = (p && p.q) || '';
        if (!q || q.trim().toLowerCase() === typed) return;
        const chip = tmpl('ac-chip');
        chip.setAttribute('data-search-drawer-suggested-term', q);
        slot(chip, 'text').textContent = '“' + q + '”';
        chip.addEventListener('click', function () { applyPhrase(q); });
        row.appendChild(chip);
      });
    }

    // Pack chips into a fixed line budget (2 lines; 1 in the md band, mirroring the
    // _search-drawer.css clamp). Greedy best-fit: a chip too wide for a line's tail is
    // deferred so a shorter later one fills the gap; the rest are dropped from the DOM.
    function packChips(row) {
      const chips = Array.prototype.slice.call(row.children);
      if (!chips.length) return;
      const W = row.clientWidth;
      if (!W) return; // not laid out yet — CSS clamp covers it; resize re-packs
      const gapX = parseFloat(getComputedStyle(row).columnGap) || 0;
      const maxLines = window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)').matches ? 1 : 2;
      const widths = chips.map(function (c) { return c.getBoundingClientRect().width; });
      const placed = new Array(chips.length).fill(false);
      const order = [];
      let linesLeft = maxLines;
      while (linesLeft > 0) {
        let used = 0, any = false;
        for (let i = 0; i < chips.length; i++) {
          if (placed[i]) continue;
          const need = used === 0 ? widths[i] : used + gapX + widths[i];
          if (need <= W + 0.5) { placed[i] = true; order.push(i); used = need; any = true; }
        }
        linesLeft--;
        if (!any) break; // nothing left fits on a fresh line
      }
      order.forEach(function (i) { row.appendChild(chips[i]); }); // DOM = packing order
      chips.forEach(function (c, i) { if (!placed[i]) c.remove(); });
    }

    // Pack now + remember the set so a resize can rebuild-and-repack.
    function fitChips(row, phrases) {
      packChips(row);
      lastFit = { row: row, phrases: phrases };
    }

    // "Trending Items" heading + 2-col bleed-x grid (replaces [data-slot=grid]).
    function renderTrending(groups) {
      const wrap = tmpl('ac-trending');
      slot(wrap, 'grid').replaceWith(renderGrid(groups, 'ac-grid-trending'));
      return wrap;
    }

    // tmplName: 'ac-grid-products' (the products tab panel) | 'ac-grid-trending'.
    // The feed carries no pre-order signal and these cards have no size pills to carry one, so the badge
    // is resolved from the product payload — only for cards already badged out of stock, deferred to idle.
    // The lookup is promised once per handle and shared: every render builds new card nodes, and each
    // must receive the answer. A rejected lookup drops out of the cache so a later render retries.
    const preorderLookups = new Map();
    function lookupPreorder(handle) {
      let pending = preorderLookups.get(handle);
      if (!pending) {
        const controller = new AbortController();
        const timer = setTimeout(function () { controller.abort(); }, STOCK_BADGE_HOLD_MS);
        pending = fetch(shopRoot() + 'products/' + encodeURIComponent(handle) + '.js', { signal: controller.signal })
          .then(function (r) { clearTimeout(timer); return r.ok ? r.json() : null; })
          .then(function (p) {
            return ((p && p.variants) || []).some(function (v) {
              const plans = v.selling_plan_allocations || [];
              return window.BroraPreorderMarket ? window.BroraPreorderMarket.anyAllowed(plans) : plans.length > 0;
            });
          })
          .catch(function () { clearTimeout(timer); preorderLookups.delete(handle); return false; });
        preorderLookups.set(handle, pending);
      }
      return pending;
    }

    // Aborts a lookup that has not answered by then, which in turn ends the hold below.
    const STOCK_BADGE_HOLD_MS = 3000;

    // The stock badge and the pre-order badge answer the same question, and the feed knows only the stock
    // half, so the badge is held until lookupPreorder answers. The container is absolutely positioned, so
    // the hold shifts nothing.
    function holdStockBadge(badges) {
      const held = Array.prototype.slice.call(badges.children).filter(function (b) {
        const kind = b.getAttribute('data-card-badge');
        return kind === 'out-of-stock' || kind === 'low-stock';
      });
      if (!held.length) return function () {};
      held.forEach(function (b) { b.classList.add('hidden'); });
      let released = false;
      return function () {
        if (released) return;
        released = true;
        held.forEach(function (b) { b.classList.remove('hidden'); });
      };
    }

    function correctPreorderBadge(card, handle) {
      if (!card || !handle) return;
      const badges = card.querySelector('[data-slot="badges"]');
      if (!card.querySelector('[data-card-badge="out-of-stock"]') || !badges) return;
      const release = holdStockBadge(badges);
      const run = function () {
        lookupPreorder(handle).then(function (hasPreorder) {
          if (!badges.isConnected) return;
          if (!hasPreorder) { release(); return; }
          const node = tmpl('card-badge');
          if (!node) { release(); return; }
          Array.prototype.slice.call(badges.children).forEach(function (b) {
            const kind = b.getAttribute('data-card-badge');
            if (kind === 'out-of-stock' || kind === 'low-stock') b.remove();
          });
          node.textContent = 'Pre-order';
          node.setAttribute('data-card-badge', 'pre-order');
          badges.insertBefore(node, badges.firstChild);
        }).catch(release);
      };
      (window.requestIdleCallback || function (cb) { setTimeout(cb, 200); })(run);
    }

    function renderGrid(groups, tmplName) {
      const grid = tmpl(tmplName);
      groups.forEach(function (ps, i) {
        // ps = a colour group; ps[0] is the main product, siblings drive the
        // swatches + hover-swap. gallery:true gives the navigable gallery, no quick-add.
        const card = window.renderVoyadoCard(ps[0], { siblings: ps, gallery: true });
        const handleMatch = ((ps[0] && ps[0].link) || '').match(/\/products\/([^/?#]+)/);
        if (handleMatch) correctPreorderBadge(card, handleMatch[1]);
        // Drawer parity: edge-aligned image, side padding on text/swatches — pr-md
        // every card, pl-md on the left column (even index). Layout tweak on the
        // cloned card (both classes auto-detected from this file).
        card.querySelectorAll('[data-slot="content"], [data-slot="swatches-row"]').forEach(function (el) {
          el.classList.add('pr-md');
          if (i % 2 === 0) el.classList.add('pl-md');
        });
        grid.appendChild(card);
      });
      return grid;
    }

    function applyPhrase(query) {
      const drawer = node.closest('[data-search-drawer]');
      if (!drawer) return;
      const input = drawer.querySelector('[data-search-drawer-input]');
      if (input) { input.value = query; input.focus(); }
      // Setting input.value in code doesn't fire search-drawer.js's input handler,
      // so swap the regions here (show results, hide default) or the fetched
      // results render into the still-hidden results host.
      const def = drawer.querySelector('[data-search-drawer-default]');
      const res = drawer.querySelector('[data-search-drawer-results]');
      if (def) def.classList.add('hidden');
      if (res) res.classList.remove('hidden');
      runFetch(query, 'PHRASE_SUGGEST');
    }

    // Drawer query event + Products/Pages tab switching, then prefetch the
    // empty/browse state from the current input value.
    document.addEventListener('search-drawer:query', function (e) {
      scheduleFetch(e.detail && e.detail.query);
    });
    // Re-pack on resize (wrapping + the md 1/2-line switch change with width). Rebuild
    // the full set first, since packing drops overflow chips. rAF-coalesced.
    let refitRaf = 0;
    window.addEventListener('resize', function () {
      if (refitRaf) return;
      refitRaf = requestAnimationFrame(function () {
        refitRaf = 0;
        if (!lastFit || !document.contains(lastFit.row)) { lastFit = null; return; }
        buildChips(lastFit.row, lastFit.phrases);
        packChips(lastFit.row);
      });
    });
    node.addEventListener('click', function (e) {
      const tab = e.target.closest('[data-search-drawer-tab]');
      if (!tab || tab.disabled) return;
      const tablist = tab.parentElement;
      const target = tab.getAttribute('data-search-drawer-tab');
      tablist.setAttribute('data-search-drawer-active-tab', target);
      tablist.querySelectorAll('[data-search-drawer-tab]').forEach(function (t) {
        t.setAttribute('aria-selected', t.getAttribute('data-search-drawer-tab') === target ? 'true' : 'false');
      });
    });
    // Enter/submit on the drawer form normally GETs routes.search_url?q=… with no
    // origin (→ ORGANIC on the search page). When the query came from a phrase chip,
    // append origin=PHRASE_SUGGEST so the search-page query is tagged; organic
    // submits fall through to native form handling untouched.
    const formEl = drawerRoot && drawerRoot.querySelector('[data-search-drawer-form]');
    if (formEl) {
      formEl.addEventListener('submit', function (e) {
        if (!phraseSuggested) return; // organic — let the browser submit natively
        const inputEl = drawerRoot.querySelector('[data-search-drawer-input]');
        const q = inputEl && inputEl.value.trim();
        if (!q) return; // empty → native required-validation handles it
        e.preventDefault();
        const base = formEl.getAttribute('action') || '';
        window.location.href = base + '?q=' + encodeURIComponent(q) + '&origin=PHRASE_SUGGEST';
      });
    }

    const drawer = node.closest('[data-search-drawer]');
    const input = drawer && drawer.querySelector('[data-search-drawer-input]');

    // Fetch on first drawer open only — not on page load. The trending grid (empty-query
    // fetchTrending) renders ~70 product images; fetching it unconditionally at boot cost every
    // page view 3+ MiB for a panel most visits never open. The opening animation covers most of
    // the round trip, and a drawer that is never opened costs no extra request.
    if (drawer) {
      new MutationObserver(function () {
        if (drawer.getAttribute('data-search-drawer-open') !== 'true') return;
        if (currentQuery) return; // typing owns the panel
        const filled = drawer.querySelector('[data-search-drawer-trending]');
        if (filled && filled.children.length) return;
        zeroTermRetries = 0;
        runFetch('', 'ORGANIC');
      }).observe(drawer, { attributes: true, attributeFilter: ['data-search-drawer-open'] });
    }
  }

  function boot() {
    document.querySelectorAll('[data-voyado-autocomplete]').forEach(init);
  }
  // Loaded from search-drawer.liquid — before the body-end Voyado globals — so
  // boot at DOMContentLoaded (after every defer script), when VoyadoClient and
  // renderVoyadoCard are defined.
  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }
})();
